/*
SELECT name as first_name, age FROM TABLE where age > 20 AND age < 30 ORDER BY age DESC LIMIT 5
SELECT name, age from TABLE JOIN name WHERE age = 20
SELECT DISTINCT Country FROM TABLE;
*/

import React from "react";

import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from "@chakra-ui/react";
import { ISelectOption } from "../../components/forms/SelectControl";
import {
  IEncryptQuery,
  IEncryptQueryConditions,
  IEncryptQuerySelectAs,
  initialEncryptQueryValues,
} from "../registries/models/Encrypt";
import { EncryptFormHandle, EncryptQueryForm } from "./EncryptQueryForm";

interface IProps {
  isOpen: boolean;
  onChange: (query: string) => void;
  onClose: () => void;
  columns: ISelectOption[];
}

export const EditQueryModal = ({
  onClose,
  onChange,
  isOpen,
  columns,
}: IProps) => {
  const formikRef = React.useRef<EncryptFormHandle>(null);

  const [formData, setFormData] = React.useState<IEncryptQuery>(
    initialEncryptQueryValues
  );

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSave = () => {
    if (formikRef) {
      formikRef.current?.submitForm();
    }
  };

  const handleOnSubmit = async (data: IEncryptQuery) => {
    setFormData(data);

    // SELECT name as first_name, age FROM TABLE where age > 20 AND age < 30 ORDER BY age DESC LIMIT 5
    let sqlStatement = "SELECT";

    // SELECT
    if (data.selects?.length) {
      data.selects.forEach((select: IEncryptQuerySelectAs, idx) => {
        if (select.isDistinct) {
          sqlStatement = `${sqlStatement} DISTINCT`;
        }

        sqlStatement = `${sqlStatement} ${select.columnName}`;

        if (select.asValue) {
          sqlStatement = `${sqlStatement} as ${select.asValue}`;
        }

        // If not last, add comma
        if (data.selects.length > idx + 1) {
          sqlStatement = `${sqlStatement},`;
        }
      });
    } else {
      sqlStatement = `${sqlStatement} *`;
    }

    sqlStatement = `${sqlStatement} FROM TABLE`;

    // JOIN ON
    const validjoinOns = data.joinOn.filter(
      (joinOnField: string) => !!joinOnField
    );
    if (validjoinOns.length) {
      sqlStatement = `${sqlStatement} JOIN`;

      validjoinOns.forEach((joinOnField: string, idx) => {
        sqlStatement = `${sqlStatement} ${joinOnField}`;

        // If not last, add comma
        if (validjoinOns.length > idx + 1) {
          sqlStatement = `${sqlStatement},`;
        }
      });
    }

    // WHERE
    if (data.conditions?.length) {
      sqlStatement = `${sqlStatement} WHERE`;

      data.conditions.forEach((condition: IEncryptQueryConditions, idx) => {
        // If not first, add logic
        if (idx > 0) {
          sqlStatement = `${sqlStatement} ${condition.logic}`;
        }

        sqlStatement = `${sqlStatement} ${condition.field} ${condition.operator} '${condition.search}'`;
      });
    }

    // ORDER BY
    const validOrderBys = data.orderBy.filter(
      (orderByField: string) => !!orderByField
    );
    if (validOrderBys.length) {
      sqlStatement = `${sqlStatement} ORDER BY`;

      validOrderBys.forEach((orderByField: string, idx) => {
        sqlStatement = `${sqlStatement} ${orderByField}`;

        // If not last, add comma
        if (validOrderBys.length > idx + 1) {
          sqlStatement = `${sqlStatement},`;
        }
      });
    }

    // DESC
    if (data.isSortDesc) {
      sqlStatement = `${sqlStatement} DESC`;
    }

    // LIMIT
    if (data.limit && parseInt(data.limit)) {
      sqlStatement = `${sqlStatement} LIMIT ${data.limit}`;
    }

    // SEMICOLON
    sqlStatement = `${sqlStatement};`;

    onChange(sqlStatement);
    handleOnClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      closeOnOverlayClick={true}
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Query</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <EncryptQueryForm
              formValues={formData}
              ref={formikRef}
              onSubmit={handleOnSubmit}
              columns={columns}
            ></EncryptQueryForm>

            {/* {isErrorCreateRegistry && (
              <Alert status="error" variant={"solid"}>
                <AlertIcon />
                <Text>Failed to create new registry, please refresh</Text>
              </Alert>
            )} */}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button onClick={onClose}>Close</Button>
            <Button
              type="submit"
              onClick={handleOnSave}
              colorScheme={"primary"}
            >
              Save Query
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
